import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';


import ResultsWidget from '../../components/stats/ResultsWidget';
import '../../components/stats/ResultsWidget.css';
import {
  getCompetitionsForRankingBuilder,
  getCompetitionSeasonsForRankingBuilder,
  getRankingCompetitionMeta,
  getWidgetConfiguration,
} from '../../../api';
import { resolveWidgetDisplayTitle } from '../../utils/widgetDisplayTitle';
import { useRef } from 'react';
import Footer from '../footer/Footer';


const DISCIPLINE_LABELS = {
  rugby: 'Rugby',
  soccer: 'Football',
};

const DISCIPLINE_OPTIONS = [
  { value: 'rugby', label: 'Rugby' },
  { value: 'soccer', label: 'Football' },
];

const getSeasonDisplayLabel = (seasonValue) => {
  const value = String(seasonValue || '').trim();
  if (!value) return '';

  if (value.includes('/')) {
    return value.split('/')[0];
  }

  return value;
};

const normalizeSeasonOptions = (rawSeasons = []) => {
  const byLabel = new Map();

  rawSeasons.forEach((seasonValue) => {
    const value = String(seasonValue || '').trim();
    if (!value) return;

    const label = getSeasonDisplayLabel(value);
    const existing = byLabel.get(label);

    if (!existing) {
      byLabel.set(label, { value, label });
      return;
    }

    if (!existing.value.includes('/') && value.includes('/')) {
      byLabel.set(label, { value, label });
    }
  });

  return Array.from(byLabel.values()).sort((a, b) =>
    b.label.localeCompare(a.label)
  );
};

const getSeasonStartYear = (seasonValue) => {
  const value = String(seasonValue || '').trim();
  if (!value) return null;

  const match = value.match(/^(\d{4})(?:\/\d{4})?$/);
  if (!match) return null;

  return Number(match[1]);
};

const isSeasonAllowedBySince = (seasonValue, seasonSince) => {
  const sinceYear = Number(String(seasonSince || '').trim());
  if (!Number.isFinite(sinceYear)) return true;

  const seasonStartYear = getSeasonStartYear(seasonValue);
  if (!Number.isFinite(seasonStartYear)) return false;

  return seasonStartYear >= sinceYear;
};

const getSeasonValues = (options = []) =>
  options.map((option) => option.value).filter(Boolean);

const applySeasonParamsToSearchParams = ({
  nextParams,
  selectedSeasonOptions = [],
  seasonMode,
  configuredSeasonSince,
}) => {
  const selectedSeasonValues = getSeasonValues(selectedSeasonOptions);

  if (selectedSeasonValues.length) {
    nextParams.set('season', selectedSeasonValues.join(','));
    nextParams.delete('seasonSince');
    return;
  }

  nextParams.delete('season');

  if (seasonMode === 'since' && configuredSeasonSince) {
    nextParams.set('seasonSince', configuredSeasonSince);
  } else {
    nextParams.delete('seasonSince');
  }
};

const normalizeDayValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized !== '' ? normalized : null;
};

const MAX_SELECTED_COMPETITIONS = 5;

const clampCompetitionIds = (value = '') =>
  String(value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTED_COMPETITIONS);

const clampCompetitionObjects = (value = []) =>
  (Array.isArray(value) ? value : []).slice(0, MAX_SELECTED_COMPETITIONS);

export default function ResultsBuilderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [widgetConfig, setWidgetConfig] = useState(null);
  const [widgetConfigLoading, setWidgetConfigLoading] = useState(false);
  const [widgetConfigError, setWidgetConfigError] = useState('');

  const discipline = searchParams.get('discipline') || '';
  const seasonParam = searchParams.get('season') || '';
  const seasonSinceParam = searchParams.get('seasonSince') || '';
  const competitionIdsParam = searchParams.get('competitionIds') || '';

  const dayParam = searchParams.get('day');
  const widgetConfigurationId = searchParams.get('widgetConfigurationId') || '';

  const [competitionInfos, setCompetitionInfos] = useState([]);

const selectedCompetitionIds = useMemo(
  () => clampCompetitionIds(competitionIdsParam),
  [competitionIdsParam]
);

  const selectedSeasonsFromUrl = useMemo(
    () =>
      seasonParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [seasonParam]
  );
      const widgetSeason =
      selectedSeasonsFromUrl.length > 0
        ? selectedSeasonsFromUrl.join(',')
        : null;

  const isMultiCompetition = selectedCompetitionIds.length > 1;

  const [seasonOptions, setSeasonOptions] = useState([]);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);

  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  const [loadingCompetitions, setLoadingCompetitions] = useState(false);
  const [competitionsError, setCompetitionsError] = useState('');
  const [title, setTitle] = useState('Résultats');

  const seasonClearedManuallyRef = useRef(false);
  const competitionClearedManuallyRef = useRef(false);


const widgetConfigResolved =
  !widgetConfigurationId || (!widgetConfigLoading && (widgetConfig || widgetConfigError));

const resolvedTitle = useMemo(() => {
  if (!widgetConfigResolved) return '';

  return resolveWidgetDisplayTitle({
    widgetConfig,
    competitionInfos,
    fallbackTitle: title,
  });
}, [widgetConfigResolved, widgetConfig, competitionInfos, title]);

  const allowedDisciplines = useMemo(() => {
  if (Array.isArray(widgetConfig?.disciplines) && widgetConfig.disciplines.length) {
    return widgetConfig.disciplines;
  }
  if (widgetConfig?.discipline) {
    return [widgetConfig.discipline];
  }
  return [];
}, [widgetConfig]);

const effectiveProvider = widgetConfig?.provider || '';

const effectiveDiscipline = useMemo(() => {
  if (!allowedDisciplines.length) {
    return discipline || '';
  }

  if (discipline && allowedDisciplines.includes(discipline)) {
    return discipline;
  }

  if (
    widgetConfig?.defaultDiscipline &&
    allowedDisciplines.includes(widgetConfig.defaultDiscipline)
  ) {
    return widgetConfig.defaultDiscipline;
  }

  if (allowedDisciplines.length === 1) {
    return allowedDisciplines[0];
  }

  return '';
}, [allowedDisciplines, discipline, widgetConfig]);

const waitingForDisciplineSelection =
  allowedDisciplines.length > 1 && !effectiveDiscipline;

  const replaceSearchParamsIfChanged = (nextParams) => {
    const current = searchParams.toString();
    const next = nextParams.toString();

    if (next !== current) {
      setSearchParams(nextParams, { replace: true });
    }
  };

const disciplineConfig = useMemo(() => {
  return widgetConfig?.filtersByDiscipline?.[effectiveDiscipline] || {};
}, [widgetConfig, effectiveDiscipline]);

const seasonMode = disciplineConfig?.seasonMode === 'since' ? 'since' : 'explicit';

const configuredSeasonSince = useMemo(() => {
  return seasonMode === 'since' && disciplineConfig?.seasonSince
    ? String(disciplineConfig.seasonSince).trim()
    : '';
}, [disciplineConfig, seasonMode]);

// const headerSeasonLabel = useMemo(() => {
//   const selectedSeasonLabels = selectedSeasons
//     .map((option) => option?.label || getSeasonDisplayLabel(option?.value))
//     .filter(Boolean);

//   if (selectedSeasonLabels.length) {
//     return selectedSeasonLabels.join(', ');
//   }

//   const urlSeasonLabels = selectedSeasonsFromUrl
//     .map((season) => getSeasonDisplayLabel(season))
//     .filter(Boolean);

//   if (urlSeasonLabels.length) {
//     return urlSeasonLabels.join(', ');
//   }

//   if (seasonMode === 'since' && configuredSeasonSince) {
//     return `depuis ${configuredSeasonSince}`;
//   }

//   return '';
// }, [
//   selectedSeasons,
//   selectedSeasonsFromUrl,
//   seasonMode,
//   configuredSeasonSince,
// ]);

const allowedSeasonValues = useMemo(
  () =>
    new Set(
      (disciplineConfig?.allowedSeasons || [])
        .map((v) => String(v).trim())
        .filter(Boolean)
    ),
  [disciplineConfig]
);

const defaultSeasonValues = useMemo(
  () =>
    (disciplineConfig?.defaultSeasons || [])
      .map((v) => String(v).trim())
      .filter(Boolean),
  [disciplineConfig]
);

const allowedCompetitionIds = useMemo(
  () =>
    new Set(
      (disciplineConfig?.allowedCompetitionIds || [])
        .map((v) => String(v).trim())
        .filter(Boolean)
    ),
  [disciplineConfig]
);

const defaultCompetitionIds = useMemo(
  () =>
    (disciplineConfig?.defaultCompetitionIds || [])
      .map((v) => String(v).trim())
      .filter(Boolean),
  [disciplineConfig]
);

useEffect(() => {
  seasonClearedManuallyRef.current = false;
}, [
  effectiveProvider,
  effectiveDiscipline,
  seasonMode,
  configuredSeasonSince,
  widgetConfigurationId,
]);

useEffect(() => {
  competitionClearedManuallyRef.current = false;
}, [
  effectiveProvider,
  effectiveDiscipline,
  seasonMode,
  configuredSeasonSince,
  selectedSeasons.map((option) => option.value).join(','),
  widgetConfigurationId,
]);


const widgetMetaReady = useMemo(() => {
  if (selectedCompetitionIds.length === 0) return true;
  if (competitionInfos.length !== selectedCompetitionIds.length) return false;

  const metaIds = competitionInfos
    .map((item) => String(item.id || '').trim())
    .filter(Boolean)
    .sort();

  const selectedIds = [...selectedCompetitionIds]
    .map((id) => String(id).trim())
    .filter(Boolean)
    .sort();

  return JSON.stringify(metaIds) === JSON.stringify(selectedIds);
}, [competitionInfos, selectedCompetitionIds]);

const canRenderWidget =
  Boolean(effectiveDiscipline) &&
  selectedCompetitionIds.length > 0 &&
  widgetMetaReady;

const showWidgetMetaLoader =
  Boolean(effectiveDiscipline) &&
  selectedCompetitionIds.length > 0 &&
  !widgetMetaReady;

  useEffect(() => {
    let ignore = false;

    const loadWidgetConfig = async () => {
      if (!widgetConfigurationId) {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError('');
        }
        return;
      }

      try {
        setWidgetConfigLoading(true);
        setWidgetConfigError('');

        const { data } = await getWidgetConfiguration(widgetConfigurationId);

        if (!ignore) {
          setWidgetConfig(data || null);
        }
      } catch (e) {
        if (!ignore) {
          setWidgetConfig(null);
          setWidgetConfigError('Impossible de charger la configuration du widget.');
        }
      } finally {
        if (!ignore) {
          setWidgetConfigLoading(false);
        }
      }
    };

    loadWidgetConfig();

    return () => {
      ignore = true;
    };
  }, [widgetConfigurationId]);


useEffect(() => {
  let ignore = false;

  const loadSeasons = async () => {
    if (!effectiveProvider || !effectiveDiscipline) {
      setSeasonOptions([]);
      setSelectedSeasons([]);
      return;
    }

    try {
      setLoadingSeasons(true);

      const { data } = await getCompetitionSeasonsForRankingBuilder({
        provider: effectiveProvider,
        discipline: effectiveDiscipline,
      });

      if (ignore) return;

      const rawSeasons = Array.isArray(data) ? data : [];

      const normalizedSeasons = normalizeSeasonOptions(rawSeasons).filter((option) => {
        if (seasonMode === 'since') {
          return isSeasonAllowedBySince(option.value, configuredSeasonSince);
        }

        return allowedSeasonValues.size === 0 || allowedSeasonValues.has(option.value);
      });

      setSeasonOptions(normalizedSeasons);

      const preselectedFromUrl = normalizedSeasons.filter((option) =>
        selectedSeasonsFromUrl.includes(option.value)
      );

      if (preselectedFromUrl.length) {
        setSelectedSeasons(preselectedFromUrl);

        if (preselectedFromUrl.length !== selectedSeasonsFromUrl.length) {
          const nextParams = new URLSearchParams(searchParams);

          applySeasonParamsToSearchParams({
            nextParams,
            selectedSeasonOptions: preselectedFromUrl,
            seasonMode,
            configuredSeasonSince,
          });

          if (effectiveDiscipline) {
            nextParams.set('discipline', effectiveDiscipline);
          }

          if (widgetConfigurationId) {
            nextParams.set('widgetConfigurationId', widgetConfigurationId);
          }

          replaceSearchParamsIfChanged
            ? replaceSearchParamsIfChanged(nextParams)
            : setSearchParams(nextParams, { replace: true });
        }
      } else if (normalizedSeasons.length && !seasonClearedManuallyRef.current) {
        const configuredDefaults = normalizedSeasons.filter((option) =>
          defaultSeasonValues.includes(option.value)
        );

        const nextSelected =
        configuredDefaults.length
          ? configuredDefaults
          : [normalizedSeasons[0]];

        setSelectedSeasons(nextSelected);

        const nextParams = new URLSearchParams(searchParams);

        applySeasonParamsToSearchParams({
          nextParams,
          selectedSeasonOptions: nextSelected,
          seasonMode,
          configuredSeasonSince,
        });

        if (effectiveDiscipline) {
          nextParams.set('discipline', effectiveDiscipline);
        }

        if (widgetConfigurationId) {
          nextParams.set('widgetConfigurationId', widgetConfigurationId);
        }

        replaceSearchParamsIfChanged
          ? replaceSearchParamsIfChanged(nextParams)
          : setSearchParams(nextParams, { replace: true });
      } else {
        setSelectedSeasons([]);
      }
    } catch (error) {
      if (!ignore) {
        setSeasonOptions([]);
        setSelectedSeasons([]);
      }
    } finally {
      if (!ignore) {
        setLoadingSeasons(false);
      }
    }
  };

  loadSeasons();

  return () => {
    ignore = true;
  };
}, [
  effectiveProvider,
  effectiveDiscipline,
  selectedSeasonsFromUrl,
  seasonSinceParam,
  searchParams,
  setSearchParams,
  widgetConfigurationId,
  seasonMode,
  configuredSeasonSince,
  allowedSeasonValues,
  defaultSeasonValues,
]);

useEffect(() => {
  let ignore = false;

  const loadCompetitions = async () => {
    if (!effectiveProvider || !effectiveDiscipline) {
      setCompetitions([]);
      setSelectedCompetitions([]);
      return;
    }

    try {
      setLoadingCompetitions(true);
      setCompetitionsError('');

      const selectedSeasonValues = getSeasonValues(selectedSeasons);

      const shouldUseSeasonSince =
        seasonMode === 'since' &&
        configuredSeasonSince &&
        selectedSeasonValues.length === 0;

      const { data } = await getCompetitionsForRankingBuilder({
        provider: effectiveProvider,
        discipline: effectiveDiscipline,
        season: shouldUseSeasonSince
          ? undefined
          : selectedSeasonValues.length
          ? selectedSeasonValues.join(',')
          : undefined,
        seasonSince: shouldUseSeasonSince ? configuredSeasonSince : undefined,
      });

      if (ignore) return;

      const normalized = (Array.isArray(data) ? data : []).filter(
        (competition) =>
          allowedCompetitionIds.size === 0 ||
          allowedCompetitionIds.has(String(competition._id))
      );

      setCompetitions(normalized);

      const preselected = normalized
      .filter((competition) =>
        selectedCompetitionIds.includes(String(competition._id))
      )
      .slice(0, MAX_SELECTED_COMPETITIONS);

      setSelectedCompetitions(preselected);

      const defaultCompetitions = normalized
      .filter((competition) =>
        defaultCompetitionIds.includes(String(competition._id))
      )
      .slice(0, MAX_SELECTED_COMPETITIONS);

      const fallbackCompetitions =
        normalized.length === 1 ? [normalized[0]] : [];

      const nextCompetitions =
        defaultCompetitions.length ? defaultCompetitions : fallbackCompetitions;

      if (selectedCompetitionIds.length && preselected.length !== selectedCompetitionIds.length) {
        const nextParams = new URLSearchParams(searchParams);

        if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
        if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);

        applySeasonParamsToSearchParams({
          nextParams,
          selectedSeasonOptions: selectedSeasons,
          seasonMode,
          configuredSeasonSince,
        });

        if (preselected.length) {
          nextParams.set(
            'competitionIds',
            preselected.map((competition) => String(competition._id)).join(',')
          );
        } else if (nextCompetitions.length) {
          setSelectedCompetitions(nextCompetitions);

          nextParams.set(
            'competitionIds',
            nextCompetitions.map((competition) => String(competition._id)).join(',')
          );
        } else {
          nextParams.delete('competitionIds');
        }

        replaceSearchParamsIfChanged(nextParams);
     } else if (
        !selectedCompetitionIds.length &&
        nextCompetitions.length &&
        !competitionClearedManuallyRef.current
      ) {
        setSelectedCompetitions(nextCompetitions);

        const nextParams = new URLSearchParams(searchParams);
        if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);
        if (widgetConfigurationId) nextParams.set('widgetConfigurationId', widgetConfigurationId);

        applySeasonParamsToSearchParams({
          nextParams,
          selectedSeasonOptions: selectedSeasons,
          seasonMode,
          configuredSeasonSince,
        });

        nextParams.set(
          'competitionIds',
          nextCompetitions.map((competition) => String(competition._id)).join(',')
        );

        setSearchParams(nextParams, { replace: true });
      }
    } catch (error) {
      if (!ignore) {
        setCompetitions([]);
        setSelectedCompetitions([]);
        setCompetitionsError('Impossible de charger les compétitions.');
      }
    } finally {
      if (!ignore) {
        setLoadingCompetitions(false);
      }
    }
  };

  loadCompetitions();

  return () => {
    ignore = true;
  };
}, [
  effectiveProvider,
  effectiveDiscipline,
  selectedSeasons,
  selectedCompetitionIds,
  allowedCompetitionIds,
  defaultCompetitionIds,
  seasonMode,
  configuredSeasonSince,
  searchParams,
  setSearchParams,
  widgetConfigurationId,
]);

  useEffect(() => {
  let ignore = false;

  const loadMeta = async () => {
    const firstCompetitionId = selectedCompetitionIds[0];

    if (!ignore) {
      setCompetitionInfos([]);
    }

    if (!firstCompetitionId) {
      if (!ignore) {
        setCompetitionInfos([]);
      }
      return;
    }

    try {
      const metas = await Promise.all(
        selectedCompetitionIds.map(async (competitionId) => {
          const { data } = await getRankingCompetitionMeta(competitionId);
          return data;
        })
      );

      if (ignore) return;

      setCompetitionInfos(
        metas
          .map((meta, index) => ({
            id: selectedCompetitionIds[index],
            name: meta?.name || '',
            season: meta?.season || '',
            nbDays: meta?.nbDays ?? null,
            defaultDay: meta?.defaultDay ?? null,
            rounds: Array.isArray(meta?.rounds) ? meta.rounds : [],
            rankingMode: meta?.rankingMode || 'classic',
            groups: Array.isArray(meta?.groups) ? meta.groups : [],
            thirdPartyId: meta?.thirdPartyId || '',
            thirdPartyEntityId: meta?.thirdPartyEntityId || '',
          }))
          .filter((item) => item.name)
      );
    } catch {
      if (!ignore) {
        setCompetitionInfos([]);
      }
    }
  };

  loadMeta();

  return () => {
    ignore = true;
  };
}, [selectedCompetitionIds]);

const handleWidgetDayChange = (nextDay) => {
  const nextParams = new URLSearchParams(searchParams);
  const normalizedDay = normalizeDayValue(nextDay);

  if (!normalizedDay) {
    nextParams.delete('day');
  } else {
    nextParams.set('day', normalizedDay);
  }

  if (widgetConfigurationId) {
      nextParams.set('widgetConfigurationId', widgetConfigurationId);
    }

  replaceSearchParamsIfChanged(nextParams);
};

const groupKeyParam = searchParams.get('groupKey') || '';

const handleWidgetGroupChange = (nextGroupKey) => {
  const value = String(nextGroupKey || '').trim();

  const nextParams = new URLSearchParams(searchParams);

  if (value) {
    nextParams.set('groupKey', value);
  } else {
    nextParams.delete('groupKey');
  }

  if (widgetConfigurationId) {
    nextParams.set('widgetConfigurationId', widgetConfigurationId);
  }

  replaceSearchParamsIfChanged(nextParams);
};

  const handleDisciplineChange = (event) => {
    const value = event.target.value;

    const nextParams = new URLSearchParams();
    if (value) nextParams.set('discipline', value);

    if (widgetConfigurationId) {
      nextParams.set('widgetConfigurationId', widgetConfigurationId);
    }

    setSelectedSeasons([]);
    setSelectedCompetitions([]);

    setSearchParams(nextParams);
  };

  const handleSeasonsChange = (_, value) => {

    seasonClearedManuallyRef.current = Array.isArray(value) && value.length === 0;
    competitionClearedManuallyRef.current = false;

    setSelectedSeasons(value);
    setSelectedCompetitions([]);

    const nextParams = new URLSearchParams();
    if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);

    applySeasonParamsToSearchParams({
        nextParams,
        selectedSeasonOptions: value,
        seasonMode,
        configuredSeasonSince,
      });

    if (widgetConfigurationId) {
      nextParams.set('widgetConfigurationId', widgetConfigurationId);
    }

    setSearchParams(nextParams);
  };

  const handleCompetitionsChange = (_, value) => {
  const nextValue = clampCompetitionObjects(value);
  competitionClearedManuallyRef.current = nextValue.length === 0;
  setSelectedCompetitions(nextValue);

  const nextParams = new URLSearchParams();
  if (effectiveDiscipline) nextParams.set('discipline', effectiveDiscipline);

  applySeasonParamsToSearchParams({
    nextParams,
    selectedSeasonOptions: selectedSeasons,
    seasonMode,
    configuredSeasonSince,
  });

  if (nextValue.length) {
    nextParams.set(
      'competitionIds',
      nextValue.map((competition) => String(competition._id)).join(',')
    );
  }

  nextParams.delete('groupKey');

  if (widgetConfigurationId) {
    nextParams.set('widgetConfigurationId', widgetConfigurationId);
  }

  setSearchParams(nextParams);
};

  const showDisciplineInput = allowedDisciplines.length > 1;

const showSeasonInput =
  Boolean(effectiveDiscipline) &&
  seasonOptions.length > 1;

  // Pour coupe du monde, a ajuster ensuite via option sur widget configuration (displaySeasonLabel)
  // par défaut sera "true"
  const displaySeasonLabel = false;

const showCompetitionInput =
  Boolean(effectiveDiscipline) &&
  competitions.length > 1;


  const showInitialLoader =
    Boolean(effectiveDiscipline) &&
    selectedCompetitionIds.length === 0 &&
    loadingCompetitions;

  const shouldShowWidgetHeader = widgetConfigResolved && Boolean(resolvedTitle);

  const shouldShowWidgetFilters =
  widgetConfigResolved &&
  (
    // shouldShowWidgetHeader ||
    showDisciplineInput ||
    showSeasonInput ||
    showCompetitionInput ||
    Boolean(widgetConfigError)
  );

  return (
    <Box className="results-builder-page">
      <Box className="results-builder-page__container">
        <Stack className="results-widget__content">
                  <Box className="results-widget__header results-builder-panel__header">
                    {resolvedTitle ? (
                      <Box className="results-widget__title-wrap">
                        <span className="results-widget__title-dot" aria-hidden="true" />

                        <Typography variant="h5" className="results-widget__title">
                          {resolvedTitle}
                        </Typography>
                      </Box>
                    ) : null}

                     { displaySeasonLabel && (!showSeasonInput && widgetSeason) ? (
                                            <Typography className="standings-widget__season">
                                              {widgetSeason}
                                            </Typography>
                                          ) : null}

                    {/* {!showSeasonInput && widgetSeason ? (
                      <Typography className="results-widget__competition-season">
                        Saison(s) : {widgetSeason}
                      </Typography>
                    ) : null} */}

                  </Box>
                </Stack>
        <Stack className="results-builder-page__stack" spacing={3}>
          {shouldShowWidgetFilters && (
            <Paper
              elevation={0}
              variant="outlined"
              className="results-builder-panel"
            >

              <Stack className="results-builder-panel__content" spacing={2}>

                {widgetConfigError ? (
                  <Alert severity="error">{widgetConfigError}</Alert>
                ) : null}

                {showDisciplineInput ? (
                  <>
                    <Box className="results-builder__filter-input">
                      <div className="results-builder__filter-header">
                        <p className="results-builder__filter-title">Sport</p>
                      </div>
                      <Divider />

                      <TextField
                        select
                        fullWidth
                        value={effectiveDiscipline}
                        onChange={handleDisciplineChange}
                      >
                        <MenuItem value="" className="results-builder-menu-item">
                          <em>Sélectionner un sport</em>
                        </MenuItem>

                        {DISCIPLINE_OPTIONS
                          .filter((option) => allowedDisciplines.includes(option.value))
                          .map((option) => (
                            <MenuItem
                              key={option.value}
                              value={option.value}
                              className="results-builder-menu-item"
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Box>

                    {waitingForDisciplineSelection ? (
                      <Alert severity="info">
                        Sélectionnez un sport pour charger les saisons, compétitions et les résultats.
                      </Alert>
                    ) : null}
                  </>
                ) : null}

                {effectiveDiscipline ? (
                  <>
                    {showSeasonInput ? (
                      <Box className="results-builder__filter-input">
                        <div className="results-builder__filter-header">
                          <p className="results-builder__filter-title">Saison</p>
                          <p className="results-builder__filter-caption">
                            Sélectionner une ou plusieurs saisons
                          </p>
                        </div>
                        <Divider />

                        <Autocomplete
                          multiple
                          options={seasonOptions}
                          value={selectedSeasons}
                          loading={loadingSeasons}
                          onChange={handleSeasonsChange}
                          getOptionLabel={(option) => option?.label || ''}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Sélectionner une ou plusieurs saisons"
                            />
                          )}
                        />
                      </Box>
                    ) : null}

                    {showCompetitionInput ? (
                      <Box className="results-builder__filter-input">
                        <div className="results-builder__filter-header">
                          <p className="results-builder__filter-title">Compétitions</p>
                          <p className="results-builder__filter-caption">
                            Sélectionner une ou plusieurs compétitions
                            <br />
                            Maximum {MAX_SELECTED_COMPETITIONS} compétitions
                          </p>
                        </div>
                        <Divider />

                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          options={competitions}
                          value={selectedCompetitions}
                          loading={loadingCompetitions}
                          onChange={handleCompetitionsChange}
                          getOptionLabel={(option) =>
                            option?.season
                              ? `${option.name} (${option.season})`
                              : option?.name || ''
                          }
                          isOptionEqualToValue={(option, value) =>
                            String(option._id) === String(value._id)
                          }
                          limitTags={5}
                          getOptionDisabled={(option) =>
                            selectedCompetitions.length >= MAX_SELECTED_COMPETITIONS &&
                            !selectedCompetitions.some(
                              (selected) => String(selected._id) === String(option._id)
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Sélectionner jusqu’à 5 compétitions"
                            />
                          )}
                        />
                      </Box>
                    ) : null}

                    {competitionsError ? (
                      <Alert severity="error">{competitionsError}</Alert>
                    ) : null}
                  </>
                ) : null}
              </Stack>
            </Paper>
          )}

          {canRenderWidget ? (
            <ResultsWidget
              discipline={effectiveDiscipline}
              competitionIds={selectedCompetitionIds}
              competitionInfos={competitionInfos}
              day={selectedCompetitionIds.length === 1 ? normalizeDayValue(dayParam) : null}
              groupKey={groupKeyParam}
              onGroupChange={handleWidgetGroupChange}
              widgetConfigurationId={widgetConfigurationId || null}
              title=""
              showCompetitionInfos={false}
              onNavigate={(url) => navigate(url)}
              onDayChange={selectedCompetitionIds.length === 1 ? handleWidgetDayChange : undefined}
            />
          ) : showInitialLoader || showWidgetMetaLoader ? (
            <Box className="results-builder-loader">
              <CircularProgress size={28} />
            </Box>
          ) : null}
           {canRenderWidget ? (
            <Footer isDSAS logoSize="l" align="center" />
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
